import { useCallback, useMemo, useState } from 'react';
import { useAppDispatch } from '../../../../../store/store';
import { useParams } from 'react-router-dom';
import { useAbilityContext } from '../../../../../components/Can/Can';
import { Action } from '../../../../../enums/action';
import { PAGE_TITLE } from '../../../../../constants/common';
import { IActionsDropdown } from '../../../../../types/tableDropdown';
import { ElementSize } from '../../../../../types/element-size';
import IconSwitcher from '../../../../../components/Icons/IconSwitcher/IconSwitcher';
import { useTranslation } from 'react-i18next';

export const useDeletedCycleContract = () => {
  const [cycleIdRestore, setCycleIdRestore] = useState<string>('');
  const { clientId } = useParams();
  const { t } = useTranslation();

  const {
    cycles: {
      restoreCycle,
      getDeletedCyclesByLocation,
      getCyclesCountByLocation,
    },
  } = useAppDispatch();

  const ability = useAbilityContext();

  const canRestoreCycle = useMemo(() => {
    return ability.can(Action.Delete, PAGE_TITLE.EDIT_CYCLE);
  }, [ability]);

  const dataRefetch = async (locationId: string) => {
    await Promise.all([
      getCyclesCountByLocation({ locationId }),
      getDeletedCyclesByLocation(locationId),
    ]);
  };

  const openSelectedCycleRestore = useCallback((cycleId: string) => {
    setCycleIdRestore(cycleId);
  }, []);

  const closeSelectedCycleRestore = useCallback(() => {
    setCycleIdRestore('');
  }, []);

  const onAcceptSelectedCycleRestore = useCallback(async () => {
    closeSelectedCycleRestore();

    await restoreCycle(cycleIdRestore);

    await dataRefetch(clientId);
  }, [cycleIdRestore, closeSelectedCycleRestore, clientId]);

  const restoreCycleActionBtn: IActionsDropdown = useMemo(() => {
    return {
      icon: () => (
        <div className="flex h-4 items-center justify-center text-success">
          <IconSwitcher size={ElementSize.XXS} />
        </div>
      ),
      name: t('commonText.restore'),
      isPinned: true,
      onClick: (cycleId: string) => {
        openSelectedCycleRestore(cycleId);
      },
      loading: false,
    };
  }, [openSelectedCycleRestore]);

  const cycleActions: IActionsDropdown[] = useMemo(() => {
    return canRestoreCycle ? [restoreCycleActionBtn] : [];
  }, [canRestoreCycle, restoreCycleActionBtn]);

  return {
    cycleActions,

    cycleIdRestore,
    closeSelectedCycleRestore,
    onAcceptSelectedCycleRestore,
  };
};
